// Stock on hand, by product (plan §5.3). A product's expiry states are shown inside its
// row, never as separate products; where it is, and how many lots, beside them.

import type { StockProductLevel } from '@enp/domain/api';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Qty,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  CalendarClock,
  ChevronRight,
  FileInput,
  PackageOpen,
  Search,
  SearchX,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { sortStates } from '../../../stock/levels.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { usePageSearch } from '../../../shell/signals.js';
import {
  BestBefore,
  FilterSelect,
  LoadMoreFooter,
  StateQtyBadges,
  StockViews,
} from '../../../stock/components.js';
import {
  NEAR_EXPIRY_DAYS,
  plainDateLabel,
  nearExpiryBy,
  stockStateLabel,
  calendarToday,
} from '../../../stock/labels.js';
import {
  clean,
  isStockFiltered,
  levelsQuery,
  locationsQuery,
  type StockSearch,
  stockSearchSchema,
} from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/stock/')({
  validateSearch: stockSearchSchema,
  component: StockPage,
});

const TABLE_LABEL = 'Stock on hand';

/** States people filter by most, before any stock has been seen. */
const COMMON_STATES = ['fresh', 'day_old', 'markdown', 'clearance', 'expired', 'default'];

const isNegative = (qty: string) => qty.trim().startsWith('-');

function LocationsCell({ level }: { level: StockProductLevel }) {
  return (
    <ul aria-label="Where" className="flex flex-col text-sm leading-snug">
      {level.locations.map((l) => (
        <li key={l.location.id} className="flex items-baseline gap-1.5 whitespace-nowrap">
          <span className="text-fg">{l.location.name}</span>{' '}
          <Qty
            value={l.qty}
            unit={level.product.base_uom}
            className={cn('text-muted', isNegative(l.qty) && 'font-medium text-state-danger')}
          />
        </li>
      ))}
    </ul>
  );
}

const col = createColumnHelper<StockProductLevel>();

// TanStack's documented DataTable typing: columns carry heterogeneous value types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const levelColumns = (today: string): ColumnDef<StockProductLevel, any>[] => [
  col.display({
    id: 'product',
    header: 'Product',
    meta: { width: 'min-w-52' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <Link
          to="/stock/$productId"
          params={{ productId: row.original.product.id }}
          // The row is the keyboard target (Enter opens); the link serves the mouse.
          tabIndex={-1}
          className="font-medium text-fg hover:underline"
        >
          {row.original.product.name}
        </Link>
        <span className="text-xs text-muted">
          {row.original.lot_count === 0
            ? 'Not tracked by lot'
            : `${row.original.lot_count} ${row.original.lot_count === 1 ? 'lot' : 'lots'}`}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'total',
    header: 'On hand',
    meta: { align: 'right', width: 'w-40' },
    cell: ({ row }) => {
      const negative = isNegative(row.original.total);
      return (
        <span className="flex flex-col items-end leading-tight">
          <Qty
            value={row.original.total}
            unit={row.original.product.base_uom}
            className={cn('font-semibold', negative ? 'text-state-danger' : 'text-fg')}
          />
          {negative && (
            <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-state-danger">
              <TriangleAlert aria-hidden className="size-3.5" />
              Sold beyond stock
            </span>
          )}
        </span>
      );
    },
  }),
  col.display({
    id: 'states',
    header: 'By expiry state',
    cell: ({ row }) => (
      <StateQtyBadges states={row.original.states} unit={row.original.product.base_uom} />
    ),
  }),
  col.display({
    // What expires first (review UI2A-10): the soonest best-before of stock that is there.
    id: 'soonest',
    header: 'Soonest best-before',
    cell: ({ row }) =>
      row.original.soonest_best_before ? (
        <BestBefore date={row.original.soonest_best_before} today={today} />
      ) : (
        <span className="text-sm text-muted">
          {isNegative(row.original.total) ? 'None on hand' : 'No best-before'}
        </span>
      ),
  }),
  col.display({
    id: 'where',
    header: 'Where',
    meta: { className: 'hidden lg:table-cell' },
    cell: ({ row }) => <LocationsCell level={row.original} />,
  }),
  col.display({
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { width: 'w-8' },
    cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
  }),
];

function StockPage() {
  useDocumentTitle('Stock');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  // Best-before judgements (days left, near expiry) use the calendar date, not the trading day.
  const today = calendarToday(me.account.timezone);
  const by = nearExpiryBy(today);
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);

  const setSearch = (patch: Partial<StockSearch>, replace = false) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace });

  // The search box types freely; the URL (and the server search) follows after a pause.
  const [text, setText] = useState(search.q ?? '');
  const pushed = useRef(search.q ?? '');
  useEffect(() => {
    if ((search.q ?? '') !== pushed.current) {
      pushed.current = search.q ?? '';
      setText(search.q ?? '');
    }
  }, [search.q]);
  useEffect(() => {
    const q = text.trim();
    if (q === pushed.current) return;
    const timer = setTimeout(() => {
      pushed.current = q;
      void navigate({ search: (prev) => clean({ ...prev, q: q || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, navigate]);

  const locations = useQuery(locationsQuery());
  const list = useInfiniteQuery({ ...levelsQuery(search, by), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = isStockFiltered(search);
  const columns = levelColumns(today);

  // States offered in the filter: the common ones plus every state on screen.
  const stateOptions = useMemo(() => {
    const keys = new Set(COMMON_STATES);
    for (const i of items) for (const s of i.states) keys.add(s.state);
    if (search.state) keys.add(search.state);
    return sortStates([...keys]).map((s) => ({ value: s, label: stockStateLabel(s) }));
  }, [items, search.state]);

  const locationOptions = (locations.data?.items ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    group: l.branch.name,
  }));

  const clearAll = () => {
    pushed.current = '';
    setText('');
    void navigate({ search: {} });
  };

  const focusFirstRow = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowDown') return;
    const row = document.querySelector<HTMLTableRowElement>(
      `table[aria-label="${TABLE_LABEL}"] tbody tr[tabindex="0"]`,
    );
    if (row) {
      e.preventDefault();
      row.focus();
    }
  };

  const firstLoad = list.isPending;
  const failed = list.isError && !list.data;

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="Nothing on hand matches"
      description={
        search.near
          ? `No lots reach their best-before in the next ${NEAR_EXPIRY_DAYS} days with these filters.`
          : search.q
            ? `No stock of a product called “${search.q}” with these filters.`
            : 'No stock fits these filters.'
      }
      action={
        <Button variant="secondary" icon={<X aria-hidden />} onClick={clearAll}>
          Clear search and filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={PackageOpen}
      title="No stock recorded yet"
      description="Stock appears here once it’s counted in, delivered or made. Start with your catalogue, then count what’s on the shelves."
      action={
        <Button asChild>
          <Link to="/catalogue">
            <FileInput aria-hidden />
            Open the catalogue
          </Link>
        </Button>
      }
    />
  );

  return (
    <>
      <PageHeader
        title="Stock"
        description="What’s on hand, in which expiry state and where. Every figure comes straight from the stock ledger."
      />
      <StockViews current="levels" />

      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Stock search"
      >
        <div className="relative w-full max-w-64">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search products"
            aria-keyshortcuts="/"
            placeholder="Search by product name"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={focusFirstRow}
            className="pl-8"
          />
        </div>
        <FilterSelect
          label="Location"
          value={search.location}
          allLabel="All locations"
          options={locationOptions}
          onChange={(v) => setSearch({ location: v })}
        />
        <FilterSelect
          label="Expiry state"
          value={search.state}
          allLabel="All states"
          options={stateOptions}
          onChange={(v) => setSearch({ state: v })}
        />
        <Button
          variant="secondary"
          aria-pressed={Boolean(search.near)}
          className={cn(search.near && 'border-primary/60 bg-primary-subtle text-primary-text')}
          icon={<CalendarClock aria-hidden />}
          onClick={() => setSearch({ near: search.near ? undefined : true })}
        >
          Near expiry
        </Button>
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clearAll}>
            Clear
          </Button>
        )}
      </div>
      {search.near && (
        <p className="mb-3 text-sm text-muted" aria-live="polite">
          Only lots with a best-before on or before{' '}
          <span className="font-medium text-fg">{plainDateLabel(by, today)}</span> (the next{' '}
          {NEAR_EXPIRY_DAYS} days), and only their quantities, including anything below zero.
        </p>
      )}

      {failed ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="Stock"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns}
          data={items}
          loading={firstLoad}
          empty={empty}
          filterable={false}
          getRowId={(l) => l.product.id}
          onRowActivate={(l) =>
            void navigate({ to: '/stock/$productId', params: { productId: l.product.id } })
          }
          footer={
            !firstLoad && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                // A product below zero isn't "in stock"; near expiry counts only near lots
                // (UI2A-214).
                noun={
                  search.near
                    ? ['product with lots near expiry', 'products with lots near expiry']
                    : ['product', 'products']
                }
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-20rem)]"
        />
      )}
    </>
  );
}
